/**
 * Workshop Tooltip Component
 * Shows workshop details on hover
 */

import React from 'react';
import { WorkshopPosition, WorkshopBasic, plantMappingUtils } from '../../services/plantMapping';

interface WorkshopTooltipProps {
  visible: boolean;
  x: number;
  y: number;
  workshop: WorkshopPosition | WorkshopBasic | null;
}

const WorkshopTooltip: React.FC<WorkshopTooltipProps> = ({
  visible,
  x,
  y,
  workshop
}) => {
  if (!visible || !workshop) {
    return null;
  }

  // Handle both WorkshopPosition and WorkshopBasic types
  const workshopData = 'workshop' in workshop ? workshop.workshop : workshop;
  const position = 'workshop' in workshop ? workshop : null;

  // Safety check for workshop data
  if (!workshopData) {
    console.error('WorkshopTooltip: workshopData is undefined', workshop);
    return null;
  }

  const tooltipInfo = plantMappingUtils.getWorkshopTooltip(workshopData);
  const categoryColor = plantMappingUtils.getSQDCMCategoryColor(workshopData.sqdcm_category);
  const statusColor = plantMappingUtils.getWorkshopStatusColor(workshopData.status);

  // Position tooltip to avoid going off screen
  const tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    left: x + 10,
    top: y - 10,
    zIndex: 1000,
    pointerEvents: 'none',
    transform: 'translateY(-100%)'
  };

  // Adjust position if too close to edges
  if (x > window.innerWidth - 300) {
    tooltipStyle.left = x - 310;
  }
  if (y < 150) {
    tooltipStyle.transform = 'translateY(10px)';
  }

  return (
    <div
      style={tooltipStyle}
      className="bg-gray-900 text-white p-3 rounded-lg shadow-lg max-w-xs animate-in fade-in duration-200"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold border-2"
          style={{
            backgroundColor: categoryColor,
            borderColor: statusColor
          }}
        >
          {workshopData.workshop_number}
        </div>
        <div>
          <div className="font-semibold text-sm">{tooltipInfo.title}</div>
          <div className="text-xs text-gray-300">{workshopData.status}</div>
        </div>
      </div>

      {/* Workshop Title */}
      <div className="text-sm mb-2 text-gray-100">
        {tooltipInfo.subtitle}
      </div>

      {/* Details */}
      <div className="space-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-400">Category:</span>
          <span 
            className="px-2 py-1 rounded text-white font-medium"
            style={{ backgroundColor: categoryColor }}
          >
            {tooltipInfo.category}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-400">Status:</span>
          <span 
            className="px-2 py-1 rounded text-white font-medium"
            style={{ backgroundColor: statusColor }}
          >
            {tooltipInfo.status}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-400">Department:</span>
          <span 
            className="px-2 py-1 rounded text-white font-medium"
            style={{ backgroundColor: '#3b82f6' }}
          >
            {workshopData.department_name || 'Unassigned'}
          </span>
        </div>

        {tooltipInfo.savings !== 'N/A' && (
          <div className="flex justify-between">
            <span className="text-gray-400">Est. Savings:</span>
            <span className="text-green-400 font-medium">{tooltipInfo.savings}</span>
          </div>
        )}

        {/* Position info (if available) */}
        {position && (
          <>
            <div className="border-t border-gray-700 pt-1 mt-2">
              <div className="flex justify-between">
                <span className="text-gray-400">Position:</span>
                <span className="text-gray-300">
                  ({(position.x * 100).toFixed(1)}%, {(position.y * 100).toFixed(1)}%)
                </span>
              </div>
              
              {position.zone && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Zone:</span>
                  <span className="text-blue-400">{position.zone}</span>
                </div>
              )}
              
              {position.last_moved_by_name && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Last moved by:</span>
                  <span className="text-gray-300">{position.last_moved_by_name}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Actions hint */}
      <div className="border-t border-gray-700 pt-2 mt-2 text-xs text-gray-400">
        {position ? 'Click for details • Drag to move' : 'Drag to position on layout'}
      </div>

      {/* Tooltip arrow */}
      <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
    </div>
  );
};

export default WorkshopTooltip;
